import MeshWriter, {
    Attr,
    AttrMap,
    MeshWriterError,
    TypedArray,
    TypedArrayConstructor
} from './MeshWriter';

/**
 * Given two TypedArray implementations, choose the larger one or whichever is
 * a Float.
 */
export const upgradeType = (A: TypedArrayConstructor, B: TypedArrayConstructor) => {
    const list = [Int8Array, Uint8Array, Uint8ClampedArray, Int16Array, Uint16Array, Int32Array, Uint32Array, Float32Array, Float64Array];
    const ai = list.indexOf(A);
    const bi = list.indexOf(B);

    console.assert(ai !== -1,
        `The type "${A}" doesn't appear to be a supported TypedArray.`);
    console.assert(bi !== -1,
        `The type "${B}" doesn't appear to be a supported TypedArray.`);

    const i = Math.max(ai, bi);
    return list[i];
}

/**
 * Find an item in an iterator. This is like Array.find, but you don't have to
 * convert the whole thing to an array. This is used in the Join operation.
 */
const find = <T>(iter: Iterable<T>, fn: (item: T) => boolean) => {
    for(const item of iter) {
        if(fn(item)) return item;
    }
}

/**
 * The Join operation concatenates a list of MeshWriter objects into a single
 * MeshWriter without transforming the constituents.
 */
export class Join extends MeshWriter {
    elements: MeshWriter[];

    constructor(elements: MeshWriter[]) {
        super();
        this.elements = elements;
    }

    get attrs() {
        const byName = new Map();
        for(const elt of this.elements) {
            for(const attr of elt.attrs) {
                let newAttr = byName.get(attr.name);
                if(newAttr) {
                    if(newAttr.size != attr.size)
                        throw new MeshWriterError(
                            `The attribute "${attr.name}" has incompatible sizes "${attr.size}" and "${newAttr.size}".`);
                    newAttr.count += attr.count;
                    newAttr.type = upgradeType(newAttr.type, attr.type);
                } else {
                    newAttr = new Attr(
                        attr.name, attr.size, attr.count, attr.type);
                    byName.set(attr.name, newAttr);
                }
            }
        }
        return new Set(byName.values());
    }

    write(attrMap: AttrMap) {
        // The set of attributes for each element, keyed by the element
        let attrsByElt: Map< MeshWriter, Set<Attr> > = new Map();
        // The mappings we will eventually pass to each element, once we've
        // placed them at the right offset in the data arrays we received
        let eltAttrMaps: Map< MeshWriter, AttrMap> = new Map();

        for(const elt of this.elements) {
            attrsByElt.set(elt, elt.attrs);
            // create empty mappings which we'll populate next
            eltAttrMaps.set(elt, new AttrMap());
        }

        // In this loop we populate the attrMaps that we will pass to each
        // element's write method.
        for(const [attr, data] of attrMap) {
            let byteOffset = 0;

            for(const elt of this.elements) {
                const eltAttrs = attrsByElt.get(elt);
                const eltAttr = find(eltAttrs, a => a.name == attr.name);
                const newData = new eltAttr.type(
                    data.buffer,
                    data.byteOffset + byteOffset,
                    eltAttr.length
                );
                byteOffset += newData.byteLength;

                const eltAttrMap = eltAttrMaps.get(elt);
                eltAttrMap.set(eltAttr, newData);
            }
        }

        for(const [elt, eltAttrMap] of eltAttrMaps.entries()) {
            elt.write(eltAttrMap);
        }
    }
}

export class Translate extends MeshWriter {
    mesh: MeshWriter;
    x: number;
    y: number;
    z: number;

    constructor(x: number, y: number, z: number, mesh: MeshWriter) {
        super();
        this.mesh = mesh;
        this.x = x;
        this.y = y;
        this.z = z;
    }

    get attrs() { return this.mesh.attrs; }

    write(attrMap: AttrMap) {
        this.mesh.write(attrMap);
        const x = this.x,
              y = this.y,
              z = this.z,
              [_, pos] = attrMap.byName('position'),
              len = pos.length;
        for(let i=0; i<len; i+=3) {
            pos[i  ] += x;
            pos[i+1] += y;
            pos[i+2] += z;
        }
    }
}

export class TranslateUV extends MeshWriter {
    mesh: MeshWriter;
    x: number;
    y: number;

    constructor(x: number, y: number, mesh: MeshWriter) {
        super();
        this.mesh = mesh;
        this.x = x;
        this.y = y;
    }

    get attrs() { return this.mesh.attrs; }

    write(attrMap: AttrMap) {
        this.mesh.write(attrMap);
        const x = this.x,
              y = this.y,
              [_, uv] = attrMap.byName('uv'),
              len = uv.length;
        for(let i=0; i<len; i+=2) {
            uv[i  ] += x;
            uv[i+1] += y;
        }
    }
}

/**
 * Note that this is a naive scaling implementation that ignores normals.
 * Applied to curved surfaces, the normals will not be scaled accurately. For
 * accurate normal scaling of curved surfaces, use a matrix transform.
 */
export class Scale extends MeshWriter {
    mesh: MeshWriter;
    x: number;
    y: number;
    z: number;

    constructor(x: number, y: number, z: number, mesh: MeshWriter) {
        super();
        this.mesh = mesh;
        this.x = x;
        this.y = y;
        this.z = z;
    }

    get attrs() { return this.mesh.attrs; }

    write(attrMap: AttrMap) {
        this.mesh.write(attrMap);
        const x = this.x,
              y = this.y,
              z = this.z,
              [_, pos] = attrMap.byName('position'),
              len = pos.length;
        for(let i=0; i<len; i+=3) {
            pos[i  ] *= x;
            pos[i+1] *= y;
            pos[i+2] *= z;
        }
    }
}

export class ScaleUV extends MeshWriter {
    mesh: MeshWriter;
    x: number;
    y: number;

    constructor(x: number, y: number, mesh: MeshWriter) {
        super();
        this.mesh = mesh;
        this.x = x;
        this.y = y;
    }

    get attrs() { return this.mesh.attrs; }

    write(attrMap: AttrMap) {
        this.mesh.write(attrMap);
        const x = this.x,
              y = this.y,
              [_, uv] = attrMap.byName('uv'),
              len = uv.length;
        for(let i=0; i<len; i+=2) {
            uv[i  ] *= x;
            uv[i+1] *= y;
        }
    }
}

export class ReverseWinding extends MeshWriter {
    mesh: MeshWriter
    constructor(mesh: MeshWriter) {
        super();
        this.mesh = mesh;
    }

    get attrs() { return this.mesh.attrs; }

    write(attrMap: AttrMap) {

        const swap = (d: TypedArray, i: number, j: number) => {
            const t = d[i];
            d[i] = d[j];
            d[j] = t;
        }

        this.mesh.write(attrMap);
        for(let [attr, d] of attrMap.entries()) {
            const
                inc = attr.size * 3,
                off = attr.size * 2,
                imax = d.length;
            for(let i=0; i < imax; i+=inc) {
                const jmax = i+attr.size
                for(let j=i; j < jmax; j++) {
                    swap(d, j, j+off);
                }
            }
        }
    }
}


/**
 * Adds a UV attribute to the underlying mesh, if it doesn't have one already.
 * Copies the X, Y coordinates of the position attribute to the UV attribute.
 */
export class XY2UV extends MeshWriter {
    mesh: MeshWriter;

    constructor(mesh: MeshWriter) {
        super();
        this.mesh = mesh;
    }

    get attrs() {
        let result = new Set(this.mesh.attrs);
        let count: number, type: TypedArrayConstructor;
        for(let attr of this.mesh.attrs) {
            if(attr.name === 'uv') {
                result.delete(attr);
            }
            else if(attr.name === 'position') {
                count = attr.count;
                type = attr.type;
            }
        }
        result.add(new Attr('uv', 2, count, type))
        return result;
    }

    write(attrMap: AttrMap) {
        this.mesh.write(attrMap);
        const
            [_posAttr, pos] = attrMap.byName('position'),
            [_uvAttr, uv] = attrMap.byName('uv'),
            len = pos.length;
        // This is a little weird, but it incremests through i and j, copying
        // each XY to UV, and skpping Z.
        for(let i=0, j=0; i < len; ) {
            uv[j++] = pos[i++]; // u = x
            uv[j++] = pos[i++]; // v = y
            i++;                // skip z
        }
    }
}


/**
 * Sets the normal for all vertexes to the given (x, y, z) triplet. Will add
 * a normal attribute if none exists.
 */
export class SetNormal extends MeshWriter {
    mesh: MeshWriter;
    x: number;
    y: number;
    z: number;

    constructor(x: number, y: number, z: number, mesh: MeshWriter) {
        super();
        this.mesh = mesh;
        this.x = x; this.y = y; this.z = z;
    }

    get attrs() {
        let result = new Set(this.mesh.attrs);
        let count: number, type: TypedArrayConstructor;
        for(let attr of this.mesh.attrs) {
            if(attr.name === 'normal') {
                result.delete(attr);
            }
            else if(attr.name === 'position') {
                count = attr.count;
                type = attr.type;
            }
        }
        result.add(new Attr('normal', 3, count, type))
        return result;
    }

    write(attrMap: AttrMap) {
        this.mesh.write(attrMap);
        const
            [_normalAttr, n] = attrMap.byName('normal'),
            len = n.length,
            x = this.x, y = this.y, z = this.z;
        for(let i=0; i < len; ) {
            n[i++] = x;  n[i++] = y;  n[i++] = z;
        }
    }
}
